import { createClient } from "contentful";
import { atom } from "jotai";
import { isHelpArticleVisible } from "@helpers/helpsiteVisibility";
import { extractDvSearchOptions } from "./dvSearchUtils";

export interface ContentSearchOption {
	label: string;
	value: string;
	urls: string[];
	snippet?: string;
	category?: string;
}

interface ContentfulModule {
	fields?: {
		headline?: string;
		header?: string;
		title?: string;
	};
}

interface ContentfulFields {
	title?: string;
	name?: string;
	pageMeta?: {
		fields?: {
			title?: string;
		};
	};
	modules?: ContentfulModule[];
	relativeUrl?: string;
	topicsRelativeUrl?: string;
	articleRelativeUrl?: string;
	articleMainHeader?: string;
	hideInProd?: boolean;
	[key: string]: unknown;
}

interface ContentfulItem {
	sys: { id: string };
	fields: ContentfulFields;
	type?: string;
}

const contentfulClient = createClient({
	space: `${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID}`,
	accessToken: `${process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN}`,
});

const removeLeadingSlash = (s: string) => s.replace(/^\//, "");

const getPageTitle = (fields: ContentfulFields): string => {
	if (Array.isArray(fields?.modules)) {
		const headerModule = fields.modules.find(
			(m: ContentfulModule) =>
				m?.fields?.headline || m?.fields?.header || m?.fields?.title,
		);
		const fromModule =
			headerModule?.fields?.headline ||
			headerModule?.fields?.header ||
			headerModule?.fields?.title;
		if (fromModule) return fromModule;
	}

	return (
		fields?.articleMainHeader ||
		fields?.pageMeta?.fields?.title ||
		fields?.name ||
		fields?.title ||
		"Unknown"
	);
};

const extractSnippets = (
	fields: ContentfulFields | Record<string, unknown>,
	termsToMatch: string[],
): string[] => {
	const snippets: { text: string; wordCount: number; sourceLength: number }[] =
		[];
	const seenTexts = new Set<string>();
	const visited = new WeakSet<object>();

	const URL_PATTERN = /\b(?:https?:\/\/|www\.)\S+|mailto:\S+/gi;
	const isUrlOnlyString = (text: string) =>
		/^https?:\/\//i.test(text) ||
		/^www\./i.test(text) ||
		/^mailto:/i.test(text) ||
		/^\/\S*$/.test(text);

	const SENTENCE_CHARS = [".", "!", "?", "\n"];
	const MAX_SENTENCE_LENGTH = 250;

	const searchNode = (node: unknown) => {
		if (Array.isArray(node)) {
			if (visited.has(node)) return;
			visited.add(node);
			for (const item of node) {
				if (typeof item === "object" && item !== null && visited.has(item)) {
					// Skip cyclic reference
					continue;
				}
				searchNode(item);
			}
			return;
		}
		if (node && typeof node === "object") {
			if (visited.has(node)) return;
			// Skip nested Contentful articles (objects with a 'sys' property)
			if (Object.prototype.hasOwnProperty.call(node, "sys")) return;
			visited.add(node);
			for (const value of Object.values(node)) {
				if (typeof value === "object" && value !== null && visited.has(value)) {
					// Skip cyclic reference
					continue;
				}
				searchNode(value);
			}
			return;
		}
		if (typeof node !== "string") return;
		if (isUrlOnlyString(node)) return;

		const lower = node.toLowerCase();
		const wordCount = termsToMatch.filter((word) =>
			lower.includes(word),
		).length;
		if (wordCount === 0) return;

		const urlRanges: Array<[number, number]> = [];
		node.replace(URL_PATTERN, (match: string, offset: number) => {
			urlRanges.push([offset, offset + match.length]);
			return match;
		});
		const isInsideUrl = (i: number) =>
			urlRanges.some(([s, e]) => i >= s && i < e);

		const findSentenceStart = (i: number): number => {
			const before = node.substring(0, i);
			const lastBoundary = Math.max(
				...SENTENCE_CHARS.map((ch) => before.lastIndexOf(ch)),
			);
			return lastBoundary + 1;
		};
		const findSentenceEnd = (i: number): number => {
			const offsets = SENTENCE_CHARS.map((ch) => node.indexOf(ch, i)).filter(
				(idx) => idx >= 0,
			);
			return offsets.length === 0 ? node.length : Math.min(...offsets) + 1;
		};

		const buildSnippetText = (index: number, matchLength: number) => {
			const sentence = node
				.substring(
					findSentenceStart(index),
					findSentenceEnd(index + matchLength),
				)
				.replace(/\n/g, " ")
				.trim();
			if (sentence && sentence.length <= MAX_SENTENCE_LENGTH) return sentence;

			const start = Math.max(0, index - 40);
			const end = Math.min(node.length, index + matchLength + 40);
			let text = node.substring(start, end).replace(/\n/g, " ");
			if (start > 0) text = `...${text}`;
			if (end < node.length) text = `${text}...`;
			return text;
		};

		for (const word of termsToMatch) {
			const wordRegex = new RegExp(
				word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
				"gi",
			);
			node.replace(wordRegex, (match: string, index: number) => {
				if (isInsideUrl(index)) return match;
				const text = buildSnippetText(index, match.length);
				if (seenTexts.has(text)) return match;
				seenTexts.add(text);
				snippets.push({ text, wordCount, sourceLength: node.length });
				return match;
			});
		}
	};

	searchNode(fields);

	return snippets
		.sort(
			(a, b) => b.wordCount - a.wordCount || b.sourceLength - a.sourceLength,
		)
		.map((s) => s.text);
};

export const searchContentAtom = atom(
	null,
	async (_get, _set, query: string): Promise<ContentSearchOption[]> => {
		if (!query || query.length < 2) {
			return [];
		}

		const [searchResults, dvResults] = await Promise.all([
			contentfulClient.getEntries({
				"sys.contentType.sys.id[in]":
					"helpsiteArticleContent,helpsiteArticles,helpsiteArticle",
				query,
				include: 3,
				limit: 15,
			}),
			contentfulClient.getEntries({
				content_type: "marketingSiteCombined",
				"fields.relativeUrl": "/domestic-violence",
				include: 5,
				limit: 1,
			}),
		]);

		const searchWords = query
			.trim()
			.split(/\s+/)
			.filter((w) => w.length >= 2)
			.map((w) => w.toLowerCase());
		const termsToMatch =
			searchWords.length > 0 ? searchWords : [query.trim().toLowerCase()];

		const dvPage = dvResults.items[0] as ContentfulItem | undefined;
		const dvOptions = dvPage
			? extractDvSearchOptions(dvPage, termsToMatch, extractSnippets)
			: [];

		const linksToEntryCache = new Map<string, ContentfulItem[]>();

		const fetchLinksToEntry = async (
			entryId: string,
		): Promise<ContentfulItem[]> => {
			const cached = linksToEntryCache.get(entryId);
			if (cached !== undefined) {
				return cached;
			}

			try {
				const linkedEntries = await contentfulClient.getEntries({
					links_to_entry: entryId,
					include: 3,
				});
				const items = (linkedEntries.items || []) as ContentfulItem[];
				linksToEntryCache.set(entryId, items);
				return items;
			} catch {
				linksToEntryCache.set(entryId, []);
				return [];
			}
		};

		interface ResolvedPage {
			url: string;
			pageTitle: string;
			category?: string;
		}

		const resolvePages = async (
			item: ContentfulItem,
			visited = new Set<string>(),
		): Promise<ResolvedPage[]> => {
			if (visited.has(item.sys.id)) return [];
			visited.add(item.sys.id);

			const { fields } = item;
			if (!isHelpArticleVisible(fields)) return [];

			if (typeof fields.relativeUrl === "string") {
				return [
					{
						url: fields.relativeUrl.trim(),
						pageTitle: getPageTitle(fields),
					},
				];
			}

			if (typeof fields.topicsRelativeUrl === "string") {
				return [
					{
						url: `/help/${removeLeadingSlash(fields.topicsRelativeUrl as string)}`,
						pageTitle: getPageTitle(fields),
					},
				];
			}

			if (typeof fields.articleRelativeUrl === "string") {
				const articleSlug = removeLeadingSlash(
					fields.articleRelativeUrl as string,
				);
				const articleTitle = getPageTitle(fields);
				const parents = await fetchLinksToEntry(item.sys.id);

				return parents
					.filter(
						(parent) => typeof parent.fields?.topicsRelativeUrl === "string",
					)
					.map((parent) => ({
						url: `/help/${removeLeadingSlash(parent.fields.topicsRelativeUrl as string)}/${articleSlug}`,
						pageTitle: articleTitle,
						category:
							typeof parent.fields.title === "string"
								? (parent.fields.title as string)
								: undefined,
					}));
			}

			if (visited.size > 5) return [];
			const parents = await fetchLinksToEntry(item.sys.id);
			const nested = await Promise.all(
				parents.map((parent) => resolvePages(parent, new Set(visited))),
			);

			return nested.flat();
		};

		const idsNeedingParents = (searchResults.items as ContentfulItem[])
			.filter(
				(item) => !item.fields?.relativeUrl && !item.fields?.topicsRelativeUrl,
			)
			.map((item) => item.sys.id);
		await Promise.all(
			Array.from(new Set(idsNeedingParents)).map(fetchLinksToEntry),
		);

		const options: ContentSearchOption[] = (
			await Promise.all(
				(searchResults.items as ContentfulItem[]).map(
					async (item): Promise<ContentSearchOption[]> => {
						try {
							const pages = await resolvePages(item);

							const seenPageUrls = new Set<string>();
							const uniquePages = pages.filter((p) => {
								if (p?.url.includes("playrgound")) {
									return false;
								}
								if (seenPageUrls.has(p.url)) return false;
								seenPageUrls.add(p.url);
								return true;
							});

							const label =
								uniquePages[0]?.pageTitle ||
								getPageTitle(item.fields) ||
								"Unknown";

							const urls = uniquePages.map((p) => p.url);
							const category = uniquePages[0]?.category;
							const snippets = extractSnippets(item.fields, termsToMatch);
							return [
								{
									label,
									value: item.sys.id,
									urls,
									snippet: snippets[0],
									category,
								},
							];
						} catch (err) {
							console.error(
								"Error resolving search option for",
								item?.sys?.id,
								err,
							);
							return [];
						}
					},
				),
			)
		).flat();

		options.push(...dvOptions);

		const seenUrls = new Set<string>();
		// Filter as before, but dedupe by first URL
		const filtered = options.filter((item) => {
			if (item.urls.length === 0) {
				return false;
			}
			if (
				!item.category &&
				!item.urls.some((url) => url.includes("domestic-violence"))
			) {
				return false;
			}
			const urlKey = item.urls[0];
			if (seenUrls.has(urlKey)) {
				return false;
			}
			seenUrls.add(urlKey);
			return true;
		});

		// Prioritize results with header (label) matches, then whole word matches, then all words matched, then by number of matches, then fallback to original order
		const isWholeWordMatch = (item: ContentSearchOption, word: string) => {
			const text = `${item.label} ${item.snippet || ""}`;
			const regex = new RegExp(
				`\\b${word.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&")}\\b`,
				"i",
			);
			return regex.test(text);
		};

		const isHeaderMatch = (item: ContentSearchOption, word: string) => {
			const label = item.label || "";
			const regex = new RegExp(
				`\\b${word.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&")}\\b`,
				"i",
			);
			return regex.test(label);
		};

		const countMatches = (item: ContentSearchOption) => {
			let count = 0;
			const text = `${item.label} ${item.snippet || ""}`.toLowerCase();
			for (const word of searchWords) {
				if (text.includes(word)) count++;
			}
			return count;
		};

		filtered.sort((a, b) => {
			// 1. Prefer items with any header (label) match
			const aHeader = searchWords.some((word) => isHeaderMatch(a, word));
			const bHeader = searchWords.some((word) => isHeaderMatch(b, word));
			if (aHeader && !bHeader) return -1;
			if (bHeader && !aHeader) return 1;

			// 2. Prefer items with any whole word match
			const aWhole = searchWords.some((word) => isWholeWordMatch(a, word));
			const bWhole = searchWords.some((word) => isWholeWordMatch(b, word));
			if (aWhole && !bWhole) return -1;
			if (bWhole && !aWhole) return 1;

			// 3. Prefer items matching all words
			const aCount = countMatches(a);
			const bCount = countMatches(b);
			if (aCount === searchWords.length && bCount !== searchWords.length)
				return -1;
			if (bCount === searchWords.length && aCount !== searchWords.length)
				return 1;

			// 4. Prefer more matches
			if (bCount !== aCount) return bCount - aCount;
			return 0;
		});
		return filtered;
	},
);
